import React, { Fragment } from 'react';
import styled from 'styled-components';
import { PoseGroup } from 'react-pose';
import { AnimatedTimelinePatientCard } from './TimelinePatientCard';
import { useCarouselIndex } from './useCarouselIndex';
import { getHighlightKey, isHighlightedReady, isReadyForPickup, paginate } from './timelinePhases';

export const CARD_GAP_REM = 0.35;
export const COLUMN_GAP_REM = 1.5;
const CAROUSEL_INTERVAL_MS = 7000;

/*
 * A phase column is rendered as three siblings placed into explicit rows of the parent grid
 * (header / cards / page dots), so every column's header, body and dots line up regardless of
 * content. Row indexes are exported for the board to size its template.
 */
export const GRID_ROW_HEADER = 1;
export const GRID_ROW_BODY = 2;
export const GRID_ROW_DOTS = 3;

/* ---- Timeline header node -------------------------------------------------------------- */

const HeaderNode = styled.header`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  min-width: 0;
  padding: 0.9em 1em;
  border-radius: 0 0 0.25rem 0.25rem;
  background-color: ${props => props.theme.colors.primary.string()};
  color: ${props => props.theme.textColor.string()};
  font-size: ${props => props.$fontSizeVw}vw;
  margin-bottom: 0.75rem;
  z-index: 1;

  @media (max-height: 900px) {
    font-size: ${props => props.$fontSizeVw * 0.85}vw;
  }
`;

const HeaderLabel = styled.div`
  min-width: 0;
  text-transform: uppercase;
  font-size: 1.15em;
  font-weight: 700;
  line-height: 1.1;
  overflow-wrap: anywhere;
`;

const HeaderLabelArabic = styled.div`
  font-size: 0.7em;
  margin-top: 0.15em;
  opacity: 0.9;
`;

const CountBadge = styled.div`
  flex: 0 0 auto;
  min-width: 2em;
  padding: 0.1em 0.5em;
  border-radius: 999px;
  text-align: center;
  font-weight: 400;
  font-size: 0.85em;
  line-height: 1.4;
  background-color: ${props => props.theme.textColor.alpha(0.18).string()};
`;

/* ---- Body / carousel ------------------------------------------------------------------- */

const Body = styled.div`
  position: relative;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
`;

const Track = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-flow: row nowrap;
  width: ${props => props.$pages * 100}%;
`;

const Page = styled.div`
  flex: 1 0 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${CARD_GAP_REM}rem;
  opacity: ${props => (props.$visible ? 1 : 0)};
  transform: translateX(
    ${props => `${(props.$idx || 0) * -100 + (props.$visible ? 0 : (props.$direction || 1) * 100)}%`}
  );
  transition: 400ms all ease-in;
  transition-delay: 100ms;
`;

const Dots = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.4rem;
  min-height: 1.25rem;
  padding: 0.5rem 0 0.25rem;
`;

const Dot = styled.span`
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 999px;
  background-color: ${props => props.theme.textColor.alpha(props.$active ? 0.9 : 0.25).string()};
  transition: background-color 300ms ease;
`;

const Empty = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 1rem;
  border-radius: 0.25rem;
  border: 2px dashed ${props => props.theme.textColor.alpha(0.12).string()};
  color: ${props => props.theme.textColor.alpha(0.35).string()};
  font-size: ${props => props.$fontSizeVw * 0.7}vw;
  font-weight: 100;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

/* ---- Component ------------------------------------------------------------------------- */

const PhaseColumn = ({
  phase,
  /** Zero-based column index in the board grid. */
  index,
  patients,
  pageSize,
  isMiddleEast,
  cardFontSizeVw,
  headerFontSizeVw,
  /** When the whole board is empty the root renders one large empty state; hide the per-column one. */
  hideEmptyState,
  /** Optional ref to the card area, used by the root to measure the vertical budget per column. */
  bodyRef,
}) => {
  const pages = paginate(patients, pageSize);
  const pageCount = patients.length > 0 ? pages.length : 0;
  const current = useCarouselIndex(pageCount, CAROUSEL_INTERVAL_MS);
  const gridColumn = index + 1;

  return (
    <Fragment>
      <HeaderNode
        $fontSizeVw={headerFontSizeVw}
        style={{ gridColumn, gridRow: GRID_ROW_HEADER }}
        aria-label={`${phase.label}: ${patients.length} patients`}
      >
        <HeaderLabel>
          {phase.label}
          {isMiddleEast && <HeaderLabelArabic>{phase.labelArabic}</HeaderLabelArabic>}
        </HeaderLabel>
        <CountBadge>{patients.length}</CountBadge>
      </HeaderNode>

      <Body ref={bodyRef} style={{ gridColumn, gridRow: GRID_ROW_BODY }}>
        {patients.length === 0 ? (
          !hideEmptyState && <Empty $fontSizeVw={cardFontSizeVw}>No patients</Empty>
        ) : (
          <Track $pages={pages.length}>
            {pages.map((page, idx) => (
              <Page
                key={idx}
                $idx={idx}
                $visible={idx === current}
                $direction={idx - current === 1 || (current - idx === pages.length - 1 && pages.length > 2) ? -1 : 1}
              >
                <PoseGroup animateOnMount>
                  {page.map((patient, i) => (
                    <AnimatedTimelinePatientCard
                      key={patient.id}
                      color={patient.color}
                      icon={patient.icon}
                      name={patient.name}
                      readyForPickup={isReadyForPickup(patient)}
                      withHighlightBorder={isHighlightedReady(patient)}
                      highlightKey={getHighlightKey(patient)}
                      zebra={i % 2 === 1}
                      fontSizeVw={cardFontSizeVw}
                    />
                  ))}
                </PoseGroup>
              </Page>
            ))}
          </Track>
        )}
      </Body>

      <Dots
        style={{ gridColumn, gridRow: GRID_ROW_DOTS }}
        aria-label={pageCount > 1 ? `Page ${current + 1} of ${pageCount}` : undefined}
      >
        {pageCount > 1 && pages.map((_, idx) => <Dot key={idx} $active={idx === current} />)}
      </Dots>
    </Fragment>
  );
};

export default PhaseColumn;
